import { Box, Button, MenuItem, Popover, Typography } from '@mui/material';
import { FC, MouseEvent, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';

import { DetailPanelMoveTarget } from './detailPanel/types';

type Props = {
  /** 選択中の地点数。2 件未満なら何も描かない */
  count: number;
  /** 移動先候補。空なら一括割付ボタンごと描かない */
  moveTargets: readonly DetailPanelMoveTarget[];
  onMove: (targetRowId: string) => void;
  onClear: () => void;
  /** 自動配車の実行中など。移動だけ止め、選択解除は残す */
  disabledReason?: string;
  screenName: string;
};

/** 2 件以上選ばないと出さない。1 件は単体の「他車両へ配車」で足りる */
const MIN_COUNT = 2;

/**
 * 選択した地点をまとめて他車両へ移すバー（logposeTech/log_web_issues#325）。
 * 置き場所は左リストの sticky ヘッダ内で、サンプル (route-planner: MapPage.tsx:1686-1715) 準拠。
 *
 * Why not 件数を「案件 N 件」にしない: 積地カードは同じ積地への再訪をマージするので、
 * 1 地点で複数案件が動く。数えているのは地点なので、そう名乗る。
 */
export const StopBulkMoveBar: FC<Props> = ({
  count, moveTargets, onMove, onClear, disabledReason, screenName,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const disabled = !!disabledReason;
  const canMove = count >= MIN_COUNT && moveTargets.length > 0;

  // バーやボタンが消えても component は unmount されないので、anchor（= 消えたボタンの DOM）
  // が残る。そのまま再表示すると、切り離された DOM を anchor にした Popover が勝手に開く。
  // 再取得や prune で選択が減ったときに起きる
  useEffect(() => {
    if (!canMove) setMenuAnchor(null);
  }, [canMove]);

  if (count < MIN_COUNT) return null;

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    ReactGA.event('click', { screen_name: screenName, button_name: '地点 一括割付' });
    setMenuAnchor(e.currentTarget);
  };

  const handleSelectTarget = (rowId: string) => {
    // ボタンを押したあとメニューが開いたままで実行中になることがある。
    // 開いている項目も押せなくする（ボタンの disabled だけでは時間差を防げない）
    if (disabled) return;
    setMenuAnchor(null);
    onMove(rowId);
  };

  return (
    <Box
      data-testid="stop-bulk-move-bar"
      sx={{
        mt: 0.75,
        mx: 1.5,
        mb: 0.75,
        px: 1,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        borderRadius: '8px',
        border: '1px solid hsl(var(--primary) / 0.4)',
        bgcolor: 'hsl(var(--primary) / 0.06)',
      }}
    >
      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'hsl(var(--primary))' }}>
        {`地点 ${count}件選択中`}
      </Box>
      {/* 候補が空のまま描くと、押しても見出しだけの空メニューが開く
          (buildMoveTargets.ts:23 / DetailPanelShell と同じ規則) */}
      {canMove && (
        <>
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            // disabled な button は MUI Tooltip の hover を拾えないので素の title で出す
            title={disabledReason}
            onClick={handleOpenMenu}
            sx={{ ml: 'auto', height: 26, fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            他車両へ一括割付
          </Button>
          <Popover
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 240, maxHeight: 320, overflowY: 'auto', p: 0.5 } } }}
          >
            <Typography variant="caption" sx={{ px: 1, py: 0.75, display: 'block', color: 'text.secondary' }}>
              移動先の車両を選択
            </Typography>
            {moveTargets.map((target) => (
              <MenuItem
                key={target.rowId}
                disabled={disabled}
                onClick={() => handleSelectTarget(target.rowId)}
                sx={{ fontSize: 13 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {target.label}
                  </Box>
                  <Box sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {target.subLabel}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Popover>
        </>
      )}
      {/* 選択を解くのは副作用が無いので実行中でも押せる。
          候補が空のときは唯一の操作になるので ml:auto で右へ寄せる */}
      <Button
        size="small"
        variant="text"
        onClick={() => {
          ReactGA.event('click', { screen_name: screenName, button_name: '地点 選択解除' });
          onClear();
        }}
        sx={{
          height: 26,
          fontSize: 11,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          ...(moveTargets.length === 0 ? { ml: 'auto' } : {}),
        }}
      >
        選択解除
      </Button>
    </Box>
  );
};
